import { extractTextContent, } from "../llm/baseAdapter";
import { KeywordsAdapter } from "../llm/keywordsAdapter";
import { SimpleAuthManager } from "../llm/authManager";
import { RuntimeToolRegistry } from "./runtimeToolRegistry";
import { isZodSchema, safeParseToolParams } from "./safeParseToolParams";
import { ToolBase } from "../agents/base/toolBase";
import { ToolDescription } from "../agents/base/toolDescription";
import { consumeStreamingToolCalls, } from "./streaming";
const SUBMIT_RESULTS_MARKER = "__runtime_submit_results__";
class RuntimeAdapterTool extends ToolBase {
    constructor(definition) {
        super();
        this.name = definition.name;
        this.description = new ToolDescription(definition.name, definition.description, (definition.schema || { type: "object", properties: {} }));
        this.paramsSchema = definition.paramsSchema;
        this.systemPromptDescription = definition.systemPromptDescription;
        this.isAgentTool = definition.isAgentTool;
        this.fieldsToCheck = [...(definition.fieldsToCheck || [])];
        this.listsToCheck = [...(definition.listsToCheck || [])];
    }
    async execute() {
        return {};
    }
}
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function tryParseArgumentObject(value) {
    try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            return parsed;
        }
    }
    catch {
        // no-op
    }
    return null;
}
function repairLikelyTruncatedJsonObject(value) {
    let candidate = value.trim();
    if (!candidate || (!candidate.startsWith("{") && !candidate.startsWith("["))) {
        return "{}";
    }
    let inString = false;
    let escaping = false;
    const stack = [];
    for (let i = 0; i < candidate.length; i += 1) {
        const ch = candidate[i];
        if (escaping) {
            escaping = false;
            continue;
        }
        if (ch === "\\" && inString) {
            escaping = true;
            continue;
        }
        if (ch === "\"") {
            inString = !inString;
            continue;
        }
        if (inString) {
            continue;
        }
        if (ch === "{" || ch === "[") {
            stack.push(ch);
            continue;
        }
        if (ch === "}" || ch === "]") {
            const expected = ch === "}" ? "{" : "[";
            if (stack.length > 0 && stack[stack.length - 1] === expected) {
                stack.pop();
            }
        }
    }
    if (inString) {
        if (escaping) {
            candidate += "\\";
        }
        candidate += "\"";
    }
    candidate = candidate.replace(/,\s*([}\]])/g, "$1");
    candidate = candidate.replace(/,\s*$/g, "");
    for (let i = stack.length - 1; i >= 0; i -= 1) {
        candidate += stack[i] === "{" ? "}" : "]";
    }
    return candidate;
}
function normalizeToolCallArguments(raw) {
    const text = typeof raw === "string" ? raw.trim() : "";
    if (!text) {
        return "{}";
    }
    const parsed = tryParseArgumentObject(text);
    if (parsed) {
        return JSON.stringify(parsed);
    }
    const repaired = repairLikelyTruncatedJsonObject(text);
    const repairedParsed = tryParseArgumentObject(repaired);
    if (repairedParsed) {
        return JSON.stringify(repairedParsed);
    }
    return "{}";
}
function hasStreamingConfig(tool) {
    if (!tool)
        return false;
    return (tool.fieldsToCheck?.length || 0) > 0 || (tool.listsToCheck?.length || 0) > 0;
}
function resolveOperationType(toolName) {
    const normalized = toolName.trim().toLowerCase();
    if (normalized === "editformat")
        return "format";
    if (normalized === "editchart")
        return "chart";
    if (normalized.startsWith("edit"))
        return "edit";
    return "default";
}
function emitOperationFieldsFromParsedArgs(callbacks, toolName, parsedArgs, fieldsToCheck = [], listsToCheck = []) {
    if (!callbacks?.onOperationField)
        return;
    for (const field of fieldsToCheck) {
        if (!(field.name in parsedArgs))
            continue;
        callbacks.onOperationField(toolName, field.name, parsedArgs[field.name]);
    }
    for (const field of listsToCheck) {
        const value = parsedArgs[field.name];
        if (!Array.isArray(value))
            continue;
        value.forEach((item, index) => {
            callbacks.onOperationField?.(toolName, `${field.name}[${index}]`, item);
        });
    }
}
function shouldEmitSubmitSummaryFallback(assistantText, summary) {
    const normalizedSummary = summary.trim();
    if (!normalizedSummary)
        return false;
    const normalizedAssistant = assistantText.trim();
    if (!normalizedAssistant)
        return true;
    return !normalizedAssistant.includes(normalizedSummary);
}
function normalizeSubmitResults(raw) {
    const normalizedResults = Array.isArray(raw.results)
        ? raw.results
            .map((item, index) => {
            if (!item || typeof item !== "object") {
                return null;
            }
            const asObj = item;
            const requirementId = typeof asObj.requirementId === "string" && asObj.requirementId.trim().length > 0
                ? asObj.requirementId
                : `req_${index + 1}`;
            const rationale = typeof asObj.rationale === "string" && asObj.rationale.trim().length > 0
                ? asObj.rationale
                : "Completed.";
            return {
                requirementId,
                relevantResults: asObj.relevantResults,
                rationale,
            };
        })
            .filter(Boolean)
        : [];
    return {
        summary: raw.summary || "Completed.",
        results: normalizedResults.length > 0
            ? normalizedResults
            : [{ requirementId: "result", rationale: "Completed.", relevantResults: {} }],
    };
}
function createSubmitResultsTool() {
    return {
        name: "submitResults",
        description: "Submit final results for this task.",
        schema: {
            type: "object",
            properties: {
                summary: { type: "string" },
                results: {
                    type: "array",
                    items: {
                        type: "object",
                        properties: {
                            requirementId: { type: "string" },
                            relevantResults: {},
                            rationale: { type: "string" },
                        },
                        required: ["requirementId", "rationale"],
                    },
                },
            },
            required: ["summary", "results"],
        },
        fieldsToCheck: [
            { name: "summary", type: "string", list_status: false, order: 0 },
        ],
        listsToCheck: [
            {
                name: "results",
                type: "array",
                item_type: "object",
                list_status: true,
                order: 1,
                check_sub_field: "requirementId",
            },
        ],
        execute: async (params) => {
            const obj = asObject(params);
            const payload = normalizeSubmitResults({
                summary: typeof obj.summary === "string" ? obj.summary : "Completed.",
                results: Array.isArray(obj.results) ? obj.results : [],
            });
            const result = {
                [SUBMIT_RESULTS_MARKER]: true,
                submittedResults: payload,
            };
            return result;
        },
    };
}
function isSubmitResultsPayload(value) {
    const obj = asObject(value);
    return obj[SUBMIT_RESULTS_MARKER] === true && !!obj.submittedResults;
}
function buildAdapterTools(tools) {
    return tools.map((tool) => new RuntimeAdapterTool(tool));
}
const IMAGE_MARKER_REGEX = /\[\[image:([a-zA-Z0-9_-]+)\]\]/g;
function toAdapterMessages(messages, imageCache) {
    return messages.map((message) => {
        const rawContent = message.content || "";
        const rawTextContent = typeof rawContent === "string" ? rawContent : "";
        const hasImageMarkers = rawTextContent.includes("[[image:");
        let content = rawContent;
        if (hasImageMarkers && imageCache) {
            const parts = [];
            let lastIndex = 0;
            let match;
            IMAGE_MARKER_REGEX.lastIndex = 0;
            while ((match = IMAGE_MARKER_REGEX.exec(rawTextContent)) !== null) {
                const [full, imageId] = match;
                const start = match.index;
                const end = start + full.length;
                const leading = rawTextContent.slice(lastIndex, start);
                if (leading) {
                    parts.push({ type: "text", text: leading });
                }
                const attachment = imageCache.get(imageId);
                if (attachment?.base64) {
                    parts.push({
                        type: "image_url",
                        image_url: { url: `data:${attachment.mimeType};base64,${attachment.base64}` },
                    });
                }
                else {
                    // If we can't resolve the image, keep the marker as text so the model still sees something.
                    parts.push({ type: "text", text: full });
                }
                lastIndex = end;
            }
            const trailing = rawTextContent.slice(lastIndex);
            if (trailing) {
                parts.push({ type: "text", text: trailing });
            }
            if (parts.some((p) => p.type === "image_url")) {
                content = parts;
            }
        }
        const converted = {
            role: message.role,
            content,
        };
        if (message.name) {
            converted.name = message.name;
        }
        if (message.tool_call_id) {
            converted.tool_call_id = message.tool_call_id;
        }
        if (Array.isArray(message.tool_calls) && message.tool_calls.length > 0) {
            converted.tool_calls = message.tool_calls.map((toolCall) => ({
                id: toolCall.id,
                type: toolCall.type,
                function: {
                    name: toolCall.function.name,
                    arguments: normalizeToolCallArguments(toolCall.function.arguments),
                },
            }));
        }
        return converted;
    });
}
function normalizeMessageHistory(messages) {
    const normalized = [];
    let cursor = 0;
    while (cursor < messages.length) {
        const message = messages[cursor];
        if (!message) {
            cursor += 1;
            continue;
        }
        if (message.role === "assistant" &&
            Array.isArray(message.tool_calls) &&
            message.tool_calls.length > 0) {
            let next = cursor + 1;
            const toolMessages = [];
            while (next < messages.length && messages[next]?.role === "tool") {
                toolMessages.push(messages[next]);
                next += 1;
            }
            const expectedIds = message.tool_calls
                .map((toolCall) => toolCall.id)
                .filter((id) => typeof id === "string" && id.length > 0);
            const returnedIds = new Set(toolMessages
                .map((toolMessage) => toolMessage.tool_call_id)
                .filter((id) => typeof id === "string" && id.length > 0));
            const hasAllToolResults = expectedIds.length > 0 && expectedIds.every((toolCallId) => returnedIds.has(toolCallId));
            if (!hasAllToolResults) {
                normalized.push({
                    ...message,
                    tool_calls: undefined,
                    content: message.content || "",
                });
                cursor = next;
                continue;
            }
            normalized.push(message);
            normalized.push(...toolMessages);
            cursor = next;
            continue;
        }
        if (message.role === "tool") {
            cursor += 1;
            continue;
        }
        normalized.push(message);
        cursor += 1;
    }
    return normalized;
}
function buildAssistantToolCalls(calls) {
    return calls.map((call) => ({
        id: call.id,
        type: "function",
        function: {
            name: call.name,
            arguments: call.arguments || "{}",
        },
    }));
}
function signatureForCalls(calls) {
    return calls.map((call) => `${call.name}:${call.arguments}`).join("||");
}
function buildToolResultMessage(call, result) {
    return {
        role: "tool",
        name: call.name,
        tool_call_id: call.id,
        content: JSON.stringify(result),
    };
}
function isJsonParseable(value) {
    const trimmed = value.trim();
    if (!trimmed) {
        return true;
    }
    try {
        JSON.parse(trimmed);
        return true;
    }
    catch {
        return false;
    }
}
function countInvalidArgumentCalls(calls) {
    return calls.reduce((count, call) => count + (isJsonParseable(call.arguments || "{}") ? 0 : 1), 0);
}
export class ToolLoopRuntime {
    constructor(keywordsAdapter) {
        this.imageAttachmentCache = new Map();
        this.imageAttachmentSeq = 0;
        this.keywordsAdapter = keywordsAdapter ?? new KeywordsAdapter(new SimpleAuthManager());
    }
    nextImageAttachmentId() {
        this.imageAttachmentSeq += 1;
        return `xlimg_${Date.now()}_${this.imageAttachmentSeq}`;
    }
    extractImageAttachmentsFromToolResult(toolResult) {
        const extracted = [];
        const walk = (value) => {
            if (!value || typeof value !== "object")
                return value;
            if (Array.isArray(value))
                return value.map(walk);
            const obj = value;
            const next = {};
            for (const [key, raw] of Object.entries(obj)) {
                const lower = key.toLowerCase();
                const isBase64ImageField = lower.endsWith("imagebase64") || lower.endsWith("image_base64");
                if (isBase64ImageField && typeof raw === "string" && raw.length > 200) {
                    const mimeKeyCandidates = [
                        key.replace(/Base64$/i, "MimeType"),
                        key.replace(/_base64$/i, "_mime_type"),
                        "chartImageMimeType",
                        "imageMimeType",
                        "mimeType",
                    ];
                    const mimeType = mimeKeyCandidates
                        .map((k) => obj[k])
                        .find((v) => typeof v === "string" && v.trim().length > 0) ||
                        "image/png";
                    const id = this.nextImageAttachmentId();
                    this.imageAttachmentCache.set(id, { base64: raw, mimeType });
                    const idKey = key.replace(/Base64$/i, "Id").replace(/_base64$/i, "_id");
                    next[idKey] = id;
                    const sheetName = typeof obj.sheetName === "string" && obj.sheetName.trim().length > 0
                        ? obj.sheetName.trim()
                        : undefined;
                    const chartName = typeof obj.chartName === "string" && obj.chartName.trim().length > 0
                        ? obj.chartName.trim()
                        : undefined;
                    const range = typeof obj.range === "string" && obj.range.trim().length > 0 ? obj.range.trim() : undefined;
                    const caption = chartName
                        ? `Chart image (${sheetName ? `${sheetName}:` : ""}${chartName})`
                        : range
                            ? `Range image (${range})`
                            : `Image attachment (${key})`;
                    extracted.push({ id, caption });
                    continue;
                }
                next[key] = walk(raw);
            }
            return next;
        };
        return { cleanedResult: walk(toolResult), extracted };
    }
    buildImageAttachmentReminderMessage(extracted) {
        const lines = [
            "<non_user_automated_reminder title=\"Tool Images\">",
            "The following images are available for multimodal inspection:",
        ];
        for (const item of extracted) {
            lines.push(`- ${item.caption}: [[image:${item.id}]]`);
        }
        lines.push("</non_user_automated_reminder>");
        return { role: "user", content: lines.join("\n") };
    }
    async run(params) {
        const { jwtToken, model, systemPrompt, userMessage, tools, priorMessages = [], signal, callbacks, maxTokens = 4096, } = params;
        const registry = new RuntimeToolRegistry();
        const submitResultsTool = createSubmitResultsTool();
        const runtimeTools = [...tools, submitResultsTool];
        const adapterTools = buildAdapterTools(runtimeTools);
        runtimeTools.forEach((tool) => registry.register(tool));
        this.keywordsAdapter.setJwtToken(jwtToken);
        const maxIterations = params.maxIterations ?? 16;
        const messages = normalizeMessageHistory([...priorMessages]);
        const hasSystemMessage = messages.some((message) => message.role === "system");
        if (!hasSystemMessage) {
            messages.push({
                role: "system",
                content: systemPrompt,
            });
        }
        messages.push({
            role: "user",
            content: userMessage,
        });
        let previousCallSignature = null;
        let repeatedCallCount = 0;
        let latestToolResults = [];
        for (let iteration = 0; iteration < maxIterations; iteration += 1) {
            console.log(`[ToolLoopRuntime] LLM request iteration ${iteration + 1}/${maxIterations} (messages=${messages.length})`);
            callbacks?.onLlmRequest?.({
                iteration: iteration + 1,
                model,
                messageCount: messages.length,
                toolCount: runtimeTools.length,
                systemPromptLength: systemPrompt.length,
                requestPayload: {
                    systemPrompt,
                    messages: [...messages],
                    toolChoice: "auto",
                    maxTokens,
                    temperature: 0.1,
                },
            });
            let assistantText = "";
            const startedToolCalls = new Set();
            const streamedSubmitSummaryByCall = new Map();
            let streamedCallIndices = new Set();
            const emitSubmitSummaryDelta = (callIndex, summaryValue) => {
                const summary = summaryValue || "";
                if (!summary)
                    return;
                const previous = streamedSubmitSummaryByCall.get(callIndex) || "";
                let delta = "";
                if (summary.startsWith(previous)) {
                    delta = summary.slice(previous.length);
                }
                else if (!previous) {
                    delta = summary;
                }
                else {
                    delta = summary;
                }
                if (!delta)
                    return;
                assistantText += delta;
                callbacks?.onChunk?.(delta);
                callbacks?.onAgentChunk?.(delta);
                streamedSubmitSummaryByCall.set(callIndex, summary);
            };
            /**
             * Directly extract the summary from accumulated submitResults JSON.
             *
             * The StreamChunkProcessor uses on-deck stabilization which requires
             * a value to remain unchanged for N consecutive parses.  A growing
             * string like `summary` changes every chunk and therefore NEVER
             * stabilizes during streaming.  This helper bypasses that system
             * entirely by parsing the accumulated JSON after every chunk and
             * emitting the delta immediately.
             */
            const tryEmitSubmitSummaryDirect = (callIndex, accumulatedArgs) => {
                try {
                    const repaired = repairLikelyTruncatedJsonObject(accumulatedArgs);
                    const parsed = JSON.parse(repaired);
                    if (typeof parsed.summary === "string" && parsed.summary) {
                        emitSubmitSummaryDelta(callIndex, parsed.summary);
                    }
                }
                catch {
                    // Ignore parse errors – JSON is still incomplete
                }
            };
            const fetchFallbackCompletion = async () => {
                const completion = await this.keywordsAdapter.complete(model, toAdapterMessages(messages, this.imageAttachmentCache), {
                    max_tokens: maxTokens,
                    temperature: 0.1,
                    signal,
                    tools: adapterTools,
                    tool_choice: "auto",
                });
                const choice = completion.choices?.[0];
                const toolCalls = choice?.message?.tool_calls || [];
                const calls = Array.isArray(toolCalls)
                    ? toolCalls
                        .map((call, index) => ({
                        index,
                        id: call.id,
                        name: call.function?.name || "",
                        arguments: call.function?.arguments || "{}",
                    }))
                        .filter((call) => call.name.length > 0)
                    : [];
                return {
                    calls,
                    text: extractTextContent(choice?.message?.content || "").trim(),
                };
            };
            const streamWithErrorCheck = (async function* (runtime) {
                for await (const chunk of runtime.keywordsAdapter.stream(model, toAdapterMessages(messages, runtime.imageAttachmentCache), {
                    max_tokens: maxTokens,
                    temperature: 0.1,
                    signal,
                    tools: adapterTools,
                    tool_choice: "auto",
                })) {
                    const choice = chunk.choices?.[0];
                    const delta = choice?.delta;
                    if (choice?.finish_reason === "error") {
                        throw new Error(extractTextContent(delta?.content || "").trim() || "Streaming error.");
                    }
                    yield chunk;
                }
            })(this);
            let streamResult;
            try {
                streamResult = await consumeStreamingToolCalls(streamWithErrorCheck, {
                    resolveTool: (toolName) => {
                        const tool = registry.get(toolName);
                        if (!tool)
                            return undefined;
                        return {
                            name: tool.name,
                            fieldsToCheck: tool.fieldsToCheck,
                            listsToCheck: tool.listsToCheck,
                            streamableOperation: tool.streamableOperation,
                            isAgentTool: tool.isAgentTool,
                        };
                    },
                    getOperationType: resolveOperationType,
                    callbacks: {
                        onTextDelta: (delta) => {
                            assistantText += delta;
                            callbacks?.onChunk?.(delta);
                            callbacks?.onAgentChunk?.(delta);
                        },
                        onToolStart: (toolName, hasStreaming, isAgentTool, callIndex) => {
                            callbacks?.onToolStart?.(toolName, hasStreaming, isAgentTool);
                            if (typeof callIndex === "number") {
                                startedToolCalls.add(callIndex);
                            }
                        },
                        onOperationField: (toolName, fieldName, value, callIndex) => {
                            callbacks?.onOperationField?.(toolName, fieldName, value);
                            if (toolName === "submitResults" &&
                                fieldName === "summary" &&
                                typeof value === "string" &&
                                typeof callIndex === "number") {
                                emitSubmitSummaryDelta(callIndex, value);
                            }
                        },
                        onToolCallDelta: ({ index, toolName, accumulatedArgs }) => {
                            if (toolName === "submitResults") {
                                tryEmitSubmitSummaryDirect(index, accumulatedArgs);
                            }
                        },
                    },
                });
            }
            catch (llmError) {
                const errorMessage = llmError instanceof Error ? llmError.message : String(llmError);
                console.error(`[ToolLoopRuntime] LLM request failed on iteration ${iteration + 1}: ${errorMessage}`);
                if (latestToolResults.length > 0) {
                    const fallbackSummary = `Completed (follow-up failed: ${errorMessage})`;
                    callbacks?.onChunk?.(fallbackSummary);
                    callbacks?.onAgentChunk?.(fallbackSummary);
                    return {
                        submit: normalizeSubmitResults({
                            summary: fallbackSummary,
                            results: latestToolResults.map((tr, i) => ({
                                requirementId: `tool_${i + 1}`,
                                rationale: `${tr.toolName} completed.`,
                                relevantResults: tr.result,
                            })),
                        }),
                        messages: [
                            ...messages,
                            { role: "assistant", content: fallbackSummary },
                        ],
                    };
                }
                throw llmError;
            }
            streamedCallIndices = streamResult.streamedCallIndices;
            let orderedCalls = streamResult.toolCalls;
            // Fallback: some providers may return tool calls only in non-stream complete responses.
            // Also fallback when streamed tool calls exist but contain malformed JSON arguments.
            // SKIP fallback when the LLM intentionally responded with text only (no tool
            // calls but has substantive text) — this is the normal direct-text-response path.
            const streamedInvalidCount = countInvalidArgumentCalls(orderedCalls);
            const hasSubstantiveText = assistantText.trim().length > 0;
            if ((!hasSubstantiveText && orderedCalls.length === 0) ||
                streamedInvalidCount > 0) {
                console.log(`[ToolLoopRuntime] Fetching fallback completion (noText=${!hasSubstantiveText}, noCalls=${orderedCalls.length === 0}, invalidArgs=${streamedInvalidCount})`);
                try {
                    const fallback = await fetchFallbackCompletion();
                    const fallbackInvalidCount = countInvalidArgumentCalls(fallback.calls);
                    if (orderedCalls.length === 0 ||
                        (fallback.calls.length > 0 && fallbackInvalidCount < streamedInvalidCount)) {
                        orderedCalls = fallback.calls;
                    }
                    if (!assistantText && fallback.text) {
                        assistantText = fallback.text;
                    }
                }
                catch (fallbackError) {
                    const errorMessage = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
                    console.error(`[ToolLoopRuntime] Fallback completion also failed: ${errorMessage}`);
                    if (latestToolResults.length > 0) {
                        const fallbackSummary = `Completed (follow-up failed: ${errorMessage})`;
                        callbacks?.onChunk?.(fallbackSummary);
                        callbacks?.onAgentChunk?.(fallbackSummary);
                        return {
                            submit: normalizeSubmitResults({
                                summary: fallbackSummary,
                                results: latestToolResults.map((tr, i) => ({
                                    requirementId: `tool_${i + 1}`,
                                    rationale: `${tr.toolName} completed.`,
                                    relevantResults: tr.result,
                                })),
                            }),
                            messages: [
                                ...messages,
                                { role: "assistant", content: fallbackSummary },
                            ],
                        };
                    }
                    throw fallbackError;
                }
            }
            const fallbackSeed = Date.now();
            orderedCalls = orderedCalls.map((call) => ({
                ...call,
                id: call.id || `tool_call_${iteration + 1}_${call.index}_${fallbackSeed}`,
            }));
            if (orderedCalls.length === 0) {
                const finalText = assistantText.trim() || "Completed.";
                callbacks?.onLlmResponse?.({
                    iteration: iteration + 1,
                    content: finalText,
                    toolCalls: [],
                    finishReason: "stop",
                });
                return {
                    submit: normalizeSubmitResults({
                        summary: finalText,
                        results: [
                            {
                                requirementId: "result",
                                rationale: "Completed.",
                                relevantResults: { response: finalText },
                            },
                        ],
                    }),
                    messages: [
                        ...messages,
                        {
                            role: "assistant",
                            content: finalText,
                        },
                    ],
                };
            }
            callbacks?.onLlmResponse?.({
                iteration: iteration + 1,
                content: assistantText,
                toolCalls: orderedCalls.map((call) => ({
                    id: call.id,
                    name: call.name,
                    arguments: call.arguments,
                })),
                finishReason: "tool_calls",
            });
            const signature = signatureForCalls(orderedCalls);
            if (signature === previousCallSignature) {
                repeatedCallCount += 1;
            }
            else {
                previousCallSignature = signature;
                repeatedCallCount = 1;
            }
            messages.push({
                role: "assistant",
                content: assistantText,
                tool_calls: buildAssistantToolCalls(orderedCalls),
            });
            latestToolResults = [];
            for (const call of orderedCalls) {
                const tool = registry.get(call.name);
                let toolResult;
                callbacks?.onToolCall?.(call.name, call.arguments || "{}");
                if (!tool) {
                    toolResult = {
                        success: false,
                        error: `Unknown tool '${call.name}'.`,
                    };
                    {
                        const { cleanedResult, extracted } = this.extractImageAttachmentsFromToolResult(toolResult);
                        messages.push(buildToolResultMessage(call, cleanedResult));
                        if (extracted.length > 0) {
                            messages.push(this.buildImageAttachmentReminderMessage(extracted));
                        }
                    }
                    continue;
                }
                const hasStreaming = hasStreamingConfig(tool);
                if (!startedToolCalls.has(call.index)) {
                    callbacks?.onToolStart?.(tool.name, hasStreaming, tool.isAgentTool);
                    startedToolCalls.add(call.index);
                }
                try {
                    let parsedArgs = {};
                    if (call.arguments && call.arguments.trim().length > 0) {
                        const raw = JSON.parse(call.arguments);
                        parsedArgs = asObject(raw);
                    }
                    if (!streamedCallIndices.has(call.index) && hasStreaming) {
                        emitOperationFieldsFromParsedArgs(callbacks, tool.name, parsedArgs, tool.fieldsToCheck || [], tool.listsToCheck || []);
                    }
                    if (tool.paramsSchema && isZodSchema(tool.paramsSchema)) {
                        const parseResult = safeParseToolParams(tool.paramsSchema, parsedArgs);
                        if (!parseResult.success) {
                            const error = parseResult.error;
                            toolResult = {
                                success: false,
                                error: `Invalid parameters: ${error}`,
                            };
                        }
                        else {
                            parsedArgs = parseResult.data;
                            toolResult = await tool.execute(parsedArgs);
                        }
                    }
                    else {
                        toolResult = await tool.execute(parsedArgs);
                    }
                }
                catch (error) {
                    toolResult = {
                        success: false,
                        error: error instanceof Error ? error.message : String(error),
                    };
                }
                callbacks?.onToolResult?.(tool.name, toolResult);
                latestToolResults.push({ toolName: tool.name, result: toolResult });
                {
                    const { cleanedResult, extracted } = this.extractImageAttachmentsFromToolResult(toolResult);
                    messages.push(buildToolResultMessage(call, cleanedResult));
                    if (extracted.length > 0) {
                        messages.push(this.buildImageAttachmentReminderMessage(extracted));
                    }
                }
                if (isSubmitResultsPayload(toolResult)) {
                    const submitSummary = toolResult.submittedResults.summary || "";
                    if (shouldEmitSubmitSummaryFallback(assistantText, submitSummary)) {
                        callbacks?.onChunk?.(submitSummary);
                        callbacks?.onAgentChunk?.(submitSummary);
                    }
                    return {
                        submit: toolResult.submittedResults,
                        messages,
                    };
                }
            }
            if (repeatedCallCount >= 2) {
                return {
                    submit: normalizeSubmitResults({
                        summary: "Completed with loop guard after repeated tool calls.",
                        results: [
                            {
                                requirementId: "loop_guard",
                                rationale: "The same tool call sequence repeated without converging. Returning latest tool outputs.",
                                relevantResults: latestToolResults,
                            },
                        ],
                    }),
                    messages,
                };
            }
        }
        return {
            submit: normalizeSubmitResults({
                summary: "Stopped before submitResults.",
                results: [
                    {
                        requirementId: "incomplete",
                        rationale: "Tool loop reached iteration limit.",
                        relevantResults: { success: false },
                    },
                ],
            }),
            messages,
        };
    }
}
